import React, { useEffect, useMemo, useState } from "react";
import EntryMoment from "../astria/EntryMoment";
import { useAstriaCopy } from "../astria/copy";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { serverErrorMessage } from "../../utils/serverError";


type Particle = {
  left: string;
  top: string;
  size: number;
  color: string;
  duration: string;
  delay: string;
  driftX: string;
  opacity: number;
};

const PARTICLE_COLORS = ["#F6B9D8", "#A9B9FF", "#B7F2FF", "#D9C4F5"];

const createParticles = (count: number): Particle[] =>
  Array.from({ length: count }, (_, i) => {
    // Deterministic-ish spread so particles never clump on a re-render.
    const seed = (i * 9301 + 49297) % 233280;
    const rand = seed / 233280;
    return {
      left: `${(rand * 92 + 4).toFixed(2)}%`,
      top: `${((i * 37) % 88) + 6}%`,
      size: 4 + ((i * 3) % 7),
      color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
      duration: `${12 + ((i * 5) % 11)}s`,
      delay: `${-((i * 1.7) % 14).toFixed(2)}s`,
      driftX: `${((i % 2 === 0 ? 1 : -1) * (12 + ((i * 4) % 26))).toFixed(0)}px`,
      opacity: 0.28 + ((i % 4) * 0.11),
    };
  });
const successAnimations = `
  @keyframes astria-rise {
    from { opacity: 0; transform: translate3d(0, 18px, 0) scale(0.97); }
    to   { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
  }
  @keyframes astria-card-in {
    from { opacity: 0; transform: translate3d(0, 28px, 0) scale(0.965); }
    to   { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
  }
  @keyframes astria-seal-in {
    0%   { opacity: 0; transform: scale(0.4); }
    60%  { opacity: 1; transform: scale(1.08); }
    80%  { transform: scale(0.97); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes astria-check-draw {
    to { stroke-dashoffset: 0; }
  }
  @keyframes astria-glow-pulse {
    0%, 100% { opacity: 0.35; transform: scale(1); }
    50%      { opacity: 0.75; transform: scale(1.14); }
  }
  @keyframes astria-halo-ring {
    0%   { opacity: 0.5; transform: scale(0.85); }
    70%  { opacity: 0; transform: scale(1.45); }
    100% { opacity: 0; transform: scale(1.45); }
  }
  @keyframes astria-drift {
    0%   { transform: translate3d(0, 0, 0) scale(1); opacity: 0; }
    12%  { opacity: var(--astria-particle-opacity, 0.5); }
    88%  { opacity: var(--astria-particle-opacity, 0.5); }
    100% { transform: translate3d(var(--astria-drift-x, 20px), -120px, 0) scale(1.25); opacity: 0; }
  }
  @keyframes astria-aurora {
    0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
    50%      { transform: translate3d(14px, -22px, 0) scale(1.08); }
  }

  .astria-anim {
    opacity: 0;
    animation: astria-rise 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  .astria-card {
    opacity: 0;
    animation: astria-card-in 1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  .astria-seal {
    opacity: 0;
    animation: astria-seal-in 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }
  .astria-check path {
    stroke-dasharray: 32;
    stroke-dashoffset: 32;
    animation: astria-check-draw 0.6s cubic-bezier(0.65, 0, 0.35, 1) forwards;
    animation-delay: 0.55s;
  }
  .astria-glow  { animation: astria-glow-pulse 3.4s ease-in-out infinite; }
  .astria-halo  { animation: astria-halo-ring 3.4s cubic-bezier(0.22, 1, 0.36, 1) infinite; }
  .astria-orb   { animation: astria-aurora 16s ease-in-out infinite; }
  .astria-particle { animation: astria-drift var(--astria-duration, 14s) linear infinite; }

  @media (prefers-reduced-motion: reduce) {
    .astria-anim,
    .astria-card,
    .astria-seal,
    .astria-check path,
    .astria-glow,
    .astria-halo,
    .astria-orb,
    .astria-particle {
      animation: none !important;
      opacity: 1;
      transform: none;
      stroke-dashoffset: 0;
    }
    .astria-particle,
    .astria-halo { opacity: 0; }
  }
`;

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { updateCurrentUser } = useAuth();
  const copy = useAstriaCopy();
  const { t } = useTranslation();
  const [entryDismissed, setEntryDismissed] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">(
    "idle",
  );
  // Stored as a translation key (or an already-localized error) so the text
  // follows the preferred language.
  const [message, setMessage] = useState(() => t("payment.verifying"));
  // Set once /payment/verify confirms this was a region-scoped plan (Journey's
  // "Unlock <region>"), so the success screen can send the user back there
  // instead of to the global plans page.
  const [unlockedRegion, setUnlockedRegion] = useState<string | null>(null);

  const particles = useMemo(() => createParticles(18), []);

  useEffect(() => {
    const sessionId = params.get("session_id");
    const userId = params.get("user_id");
    const planId = params.get("plan_id");

    if (!sessionId) {
      setStatus("error");
      setMessage(t("payment.missingSession"));
      return;
    }

    const verifyPayment = async () => {
      try {
        setStatus("loading");
        const verifyRes = await api.post("/payment/verify", {
          session_id: sessionId,
          user_id: userId || undefined,
          plan_id: planId || undefined,
        });
        try {
          const rawUser = localStorage.getItem("user");
          const parsedUser = rawUser ? JSON.parse(rawUser) : null;
          if (parsedUser) {
            const data = verifyRes?.data || {};
            // console.log("Payment verification response:", data);
            const updatedUser = {
              ...parsedUser,
              subscriptionId: data.subscriptionId ?? parsedUser.subscriptionId,
              subscriptionStartDate:
                data.subscriptionStartDate ?? parsedUser.subscriptionStartDate,
              subscriptionEndDate:
                data.subscriptionEndDate ?? parsedUser.subscriptionEndDate,
              subscriptionStatus:
                data.subscriptionStatus ?? parsedUser.subscriptionStatus,
              // Set only for a region-scoped plan (Journey's "Unlock <region>") —
              // absent from the response for a global subscription plan, so the
              // ?? falls back to what the user already had.
              plan: data.plan ?? parsedUser.plan,
              region: data.region ?? parsedUser.region,
              unlockedRegions: data.unlockedRegions ?? parsedUser.unlockedRegions,
              // Only present when the purchase was the Energy Match add-on.
              energyMatchAccess:
                data.energyMatchAccess ?? parsedUser.energyMatchAccess,
            };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            updateCurrentUser({
              subscriptionId: updatedUser.subscriptionId,
              subscriptionStartDate: updatedUser.subscriptionStartDate,
              subscriptionEndDate: updatedUser.subscriptionEndDate,
              subscriptionStatus: updatedUser.subscriptionStatus,
              plan: updatedUser.plan,
              region: updatedUser.region,
              unlockedRegions: updatedUser.unlockedRegions,
              energyMatchAccess: updatedUser.energyMatchAccess,
            } as any);
            if (data.region) setUnlockedRegion(data.region);
          }
        } catch (storageErr) {
          console.error(
            "Failed to update local user subscription:",
            storageErr,
          );
        }
        setStatus("done");
        setMessage(t("payment.confirmed"));
      } catch (err: any) {
        setStatus("error");
        setMessage(serverErrorMessage(err, "payment.verifyFailed"));
      }
    };

    verifyPayment();
  }, [params]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F6EBDD] text-[#2D1B44]">
      <style>{successAnimations}</style>
      {status === "done" && !entryDismissed && (
        <EntryMoment
          headline={copy("proEntry")}
          subheadline={copy("proEntrySub")}
          glow="soft-cosmic"
          onDone={() => setEntryDismissed(true)}
        />
      )}
        <div className="astria-orb pointer-events-none absolute left-[-8rem] top-[-6rem] h-[28rem] w-[28rem] rounded-full bg-[#F6B9D8] blur-3xl opacity-70" />
        <div
          className="astria-orb pointer-events-none absolute bottom-[-8rem] right-[-6rem] h-[30rem] w-[30rem] rounded-full bg-[#A9B9FF] blur-3xl opacity-60"
          style={{ animationDelay: "-5s", animationDuration: "19s" }}
        />
        <div
          className="astria-orb pointer-events-none absolute right-[6%] top-[12%] h-[22rem] w-[22rem] rounded-full bg-[#B7F2FF] blur-3xl opacity-50"
          style={{ animationDelay: "-9s", animationDuration: "22s" }}
        />

        {/* Soft floating gradient particles */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {particles.map((p, i) => (
            <span
              key={i}
              className="astria-particle absolute rounded-full blur-[2px]"
              style={
                {
                  left: p.left,
                  top: p.top,
                  height: `${p.size}px`,
                  width: `${p.size}px`,
                  background: `radial-gradient(circle at 30% 30%, #ffffff, ${p.color})`,
                  animationDuration: p.duration,
                  animationDelay: p.delay,
                  "--astria-drift-x": p.driftX,
                  "--astria-particle-opacity": p.opacity,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.55) 0 1px, transparent 2px), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.4) 0 1px, transparent 2px), radial-gradient(circle at 30% 80%, rgba(255,255,255,0.35) 0 1px, transparent 2px), radial-gradient(circle at 70% 75%, rgba(255,255,255,0.45) 0 1px, transparent 2px)",
          }}
        />

      <div className="relative mx-auto flex min-h-screen max-w-4xl items-center justify-center px-6 py-16">
        <div className="astria-card relative w-full max-w-2xl overflow-hidden rounded-[32px] border border-white/60 bg-white/75 p-10 text-center shadow-[0_24px_60px_rgba(45,27,68,0.16)] backdrop-blur">
          <div className="pointer-events-none absolute -right-14 top-10 h-32 w-32 rounded-full border border-[#D9C4F5]/60 bg-white/30 blur-[0.5px]" />
          <div className="pointer-events-none absolute -left-10 bottom-12 h-24 w-24 rounded-full border border-[#BEE7FF]/70 bg-white/20 blur-[0.5px]" />

          <div
            className="astria-anim flex flex-col items-center gap-2"
            style={{ animationDelay: "0.15s" }}
          >
            <img
              src={"/logo_2.png"}
              alt="Astria"
              className="h-14 w-auto object-contain"
            />
            <span className="text-sm font-semibold uppercase tracking-[0.4em] text-[#6D4C92]">
              Astria
            </span>
          </div>

          {/* Success seal: glowing pulse + drawn checkmark */}
          <div
            className="astria-seal relative mx-auto mt-6 flex h-24 w-24 items-center justify-center"
            style={{ animationDelay: "0.35s" }}
          >
            <span className="astria-glow pointer-events-none absolute inset-0 rounded-full bg-[#C9A6F2] blur-xl" />
            <span className="astria-halo pointer-events-none absolute inset-0 rounded-full border border-[#6D4C92]/40" />
            <span
              className="astria-halo pointer-events-none absolute inset-0 rounded-full border border-[#6D4C92]/25"
              style={{ animationDelay: "1.7s" }}
            />
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-[#F2E7FF] shadow-[0_0_30px_rgba(109,76,146,0.18)]">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#2D1B44] text-[#F6EBDD] shadow-lg">
                <svg
                  className="astria-check h-8 w-8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12.5 10 17.5 19 7.5" />
                </svg>
              </div>
            </div>
          </div>

          <h1
            className="astria-anim mt-6 font-['Fraunces'] text-4xl font-semibold"
            style={{ animationDelay: "0.55s" }}
          >
            {t("payment.successTitle")}
          </h1>
          <p
            className="astria-anim mt-3 font-['Manrope'] text-base text-[#2D1B44]/70"
            style={{ animationDelay: "0.7s" }}
          >
            {message}
          </p>

          {status === "loading" ? (
            <div
              className="astria-anim mt-6 text-sm text-[#2D1B44]/60"
              style={{ animationDelay: "0.8s" }}
            >
              {t("payment.pleaseWait")}
            </div>
          ) : null}

          {status === "done" ? (
            <div
              className="astria-anim mt-8 flex justify-center"
              style={{ animationDelay: "0.85s" }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-[#E6D7F5] bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#6D4C92]">
                <span className="astria-glow h-1.5 w-1.5 rounded-full bg-[#6D4C92] shadow-[0_0_10px_rgba(109,76,146,0.7)]" />
                {t("payment.subscriptionActive")}
              </div>
            </div>
          ) : null}

          <div
            className="astria-anim mt-10 flex flex-wrap justify-center gap-3"
            style={{ animationDelay: "1s" }}
          >
            <button
              onClick={() =>
                navigate("/journey")
              }
              className="rounded-full border border-[#2D1B44]/20 px-5 py-2 text-sm font-semibold text-[#2D1B44] transition duration-300 hover:-translate-y-0.5 hover:border-[#2D1B44]/40 hover:shadow-[0_10px_24px_rgba(45,27,68,0.10)]"
            >
              {t("payment.backToJourney")}
            </button>
            <button
              onClick={() => navigate("/")}
              className="rounded-full bg-[#2D1B44] px-5 py-2 text-sm font-semibold text-[#F6EBDD] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(45,27,68,0.28)]"
            >
              {t("payment.goHome")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
